import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import LoadingIndicator from 'v2/common/LoadingIndicator/LoadingIndicator';
import AddNewFileForm from 'v2/DataFiles/AddNewFileForm/AddNewFileForm';
import DataFilesTable from 'v2/DataFiles/DataFilesTable/DataFilesTable';
import DataFileUploader from 'v2/DataFiles/DataFileUploader/DataFileUploader';
import { DataFileEntity, DataFileForm } from 'v2/DataFiles/models';
import useDatFileForm from 'v2/hooks/useDatFileForm';
import { StrapiApiError } from 'v2/models';
import { geFileData, onFileSelected } from 'v2/utils/fileUtils';
import restFactory from 'v2/utils/restFactory';
import styles from './proposalAttachFilesModal.module.scss';

interface ProposalAttachFilesModalProps {
  show: boolean;
  onHide: () => void;
  onFilesAdded: (files: DataFileEntity[]) => void;
  selectedFiles: DataFileEntity[];
}

const ProposalAttachFilesModal = ({
  show,
  onHide,
  onFilesAdded,
  selectedFiles,
}: ProposalAttachFilesModalProps) => {
  const [selectedDataFiles, setSelectedDataFiles] = useState<
    DataFileEntity[] | null
  >(selectedFiles);
  const [newFile, setNewFile] = useState<File | null>(null);
  const {
    errors,
    handleSubmit,
    register,
    onSubmit,
    isLoading: submitInProgress,
    reset,
  } = useDatFileForm(newFile, null);
  const queryClient = useQueryClient();
  const { data: dataFiles, isLoading } = useQuery(
    ['dataFiles'],
    async () => {
      const response = await restFactory.get<DataFileEntity[]>('data-files');
      return response.sort((itemA, itemB) => (itemA.id > itemB.id ? -1 : 1));
    },
    {
      onError: (error: StrapiApiError) => {
        toast.error(`Error loading data files. ${error.message}`);
      },
      refetchOnWindowFocus: false,
      retry: false,
    }
  );
  useEffect(() => {
    if (selectedFiles !== selectedDataFiles) {
      setSelectedDataFiles(selectedFiles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFiles, show]);

  const onNewFileSelected = (file: File | undefined) => {
    if (file) {
      setNewFile(file);
    }
  };

  const onDrop = onFileSelected(onNewFileSelected);

  const _onSubmit = async (formData: DataFileForm) => {
    const savedFile = await onSubmit(formData);
    queryClient.invalidateQueries('dataFiles');
    setNewFile(null);
    setSelectedDataFiles([
      ...(selectedDataFiles || []),
      savedFile,
    ] as DataFileEntity[]);
    reset();
  };

  const renderAddFileForm = () => {
    return (
      <form
        className="py-5 px-4 bg-light"
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(_onSubmit)(e);
        }}
      >
        <h1 className="bg-light">Upload File</h1>
        <Modal.Body
          className={classnames('px-0 pb-0 pt-3 bg-light', styles.newFileForm)}
        >
          <AddNewFileForm
            fileData={geFileData(newFile as File)}
            onDrop={onDrop}
            register={register}
            errors={errors}
          />
        </Modal.Body>

        <Modal.Footer>
          <input
            className="btn-secondary-dark btn btn-secondary"
            type="button"
            value="Cancel"
            onClick={() => setNewFile(null)}
          />
          <button
            className={classnames('btn btn-primary', styles.addFileBtn)}
            type="submit"
            disabled={submitInProgress}
          >
            {submitInProgress ? (
              <LoadingIndicator className={styles.loadingIndicator} />
            ) : (
              'Add File'
            )}
          </button>
        </Modal.Footer>
      </form>
    );
  };

  const renderTable = () => {
    return (
      <>
        <Modal.Body className={classnames('p-0', styles.renderTable_modalBody)}>
          <div className={styles.tableContainer}>
            <DataFilesTable
              list={dataFiles || []}
              showFilesCounter={false}
              selectedRows={selectedDataFiles}
              setSelectedRows={setSelectedDataFiles}
              showCheckBox={true}
            />
          </div>

          <div className="bg-light px-3 d-flex align-items-center justify-content-center">
            <DataFileUploader
              className="m-0"
              onFileSelected={onNewFileSelected}
            />
          </div>
        </Modal.Body>

        <Modal.Footer className={styles.modalFooter}>
          <Button
            variant="secondary"
            className={classnames('btn-secondary-dark', styles.btnCancel)}
            onClick={onHide}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={isLoading}
            onClick={() => onFilesAdded(selectedDataFiles || [])}
            aria-label="selectDataFileProviderResponse"
            className={styles.btnSelect}
          >
            Select
          </Button>
        </Modal.Footer>
      </>
    );
  };

  return (
    <Modal
      className={styles.root}
      show={show}
      onHide={onHide}
      scrollable
      animation={false}
      aria-label="attachFilesModal"
    >
      {newFile ? renderAddFileForm() : renderTable()}
    </Modal>
  );
};

export default ProposalAttachFilesModal;
